import {
  Paper,
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useState, useEffect } from "react";
import styles from "./../../styles/charts/chartStyle.module.css";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {
  getChartBottomIcons,
  getChartInfoIcons,
} from "./twitter/TwitterWordCloud";
import BubbleChart from "./protoCharts/BubbleChart";
import {
  useAllChallengeNames,
  useAllFundNames,
  useAllProposersData,
} from "../../lib/hooks";
import { underscoreToCap } from "../../lib/util";
import { proposerFormType } from "../../lib/proposerAPI";
import { useNavigate } from "react-router-dom";
import { desc } from "../../lib/chartDescription";

const ProposerBubblePlot = () => {
  const [selectedX, setSelectedX] = useState<string>("average_rating");
  const [selectedY, setSelectedY] = useState<string>("total_result_votes");
  const [selectedZ, setSelectedZ] = useState<string>("funded_amount");
  const [fund_id, setFund_id] = useState<any[]>([]);
  const [challenge_id, setChallenge_id] = useState<any[]>([]);
  const [formData, setFormData] = useState<proposerFormType>({
    num_per_page: 99999999999,
    page_number: 1,
    fund_id: "All",
    challenge_id: "All",
  });

  useEffect(() => {
    setFormData((prevState: any) => ({
      ...prevState,
      fund_id: fund_id.length === 0 ? "All" : fund_id.map((item) => item.id),
      challenge_id:
        challenge_id.length === 0 ? "All" : challenge_id.map((item) => item.id),
    }));
  }, [fund_id, challenge_id]);

  const {
    allChallengeNames,
    loading: allChallengeNameLoading,
    error: allChallengeNameError,
  } = useAllChallengeNames();

  let {
    loading: fundNameLoading,
    allFundNames,
    error: fundNamError,
  } = useAllFundNames();

  let challenge_dropDownOption =
    !fundNameLoading &&
    !allChallengeNameLoading &&
    allChallengeNameError === null &&
    fundNamError === null
      ? fund_id.length > 0
        ? allChallengeNames
            .filter((item) =>
              fund_id.map((item) => item.id).includes(item.fund_id)
            )
            .map((item) => {
              return {
                label: `${item.fund_name}:${item.challenge_name}`,
                id: item.challenge_id,
              };
            })
        : allChallengeNames.map((item) => {
            return {
              label: `${item.fund_name}:${item.challenge_name}`,
              id: item.challenge_id,
            };
          })
      : [];

  useEffect(() => {
    // The following checks handle the following case
    //1. fund4 is selected and challenge from fund4 is selected. If fund is deselected , then challenge should deselect
    //2. fund4 and fund5 is selected. challenge from fund4 is selected. If fund5 is removed , then it should not clear the challenge
    // but if fund4 is remove then it should clear challenge
    if (challenge_id.length > 0) {
      let newArr = challenge_id.filter((challenge) =>
        fund_id
          .map((item) => item.id)
          .includes(
            allChallengeNames.filter(
              (allChallenge) => allChallenge.challenge_id === challenge.id
            )[0]?.fund_id
          )
      );
      setChallenge_id(newArr);
    }
    //eslint-disable-next-line
  }, [fund_id.length]);

  const { loading, proposerData, error } = useAllProposersData(formData);

  let data: any[] = [];

  if (!loading && !error) {
    proposerData.items.forEach((proposer: any) => {
      data.push({
        p_id: proposer.user_id,
        x: proposer.quick_stats_data[selectedX],
        y: proposer.quick_stats_data[selectedY],
        z: proposer.quick_stats_data[selectedZ],
      });
    });
  }

  function genTitle() {
    return "Proposer Bubble Plot";
  }

  let navigate = useNavigate();
  const routeChange = (path: string) => {
    navigate(path);
  };

  function jumpToProposal(proposalID: number) {
    routeChange(`/proposer/${proposalID}`);
  }

  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  const z_names = [
    // "total_result_votes",
    "total_up_votes",
    "total_down_votes",
    "number_of_proposals",
    "average_rating",
    "amount_requested",
    "funded_amount",
    "funded_proposals",
    "highest_proposal_rating",
    "highest_amount_requested",
    "conversion_percentage",
    "funding_percentage",
  ];
  const names = [
    "total_result_votes",
    "total_up_votes",
    "total_down_votes",
    "number_of_proposals",
    "average_rating",
    "amount_requested",
    "funded_amount",
    "funded_proposals",
    "highest_proposal_rating",
    "highest_amount_requested",
    "conversion_percentage",
    "funding_percentage",
  ];

  const errorHandler = () => {
    setFund_id([]);
    setFormData({
      num_per_page: 99999999999,
      page_number: 1,
      fund_id: "All",
      challenge_id: "All",
    });
  };

  const content = (showMaxButton: boolean) => {
    return (
      <Paper elevation={3} className={styles.chartContainer}>
        <div className="horizontalCenter">
          <Typography className={styles.chartHeader}>{genTitle()}</Typography>
        </div>

        <div className="horizontalCenter">
          <div className={styles.flexibleControlRow}>
            <FormControl
              className={styles.flexibleformControlFullWidth}
              size="small"
            >
              <Autocomplete
                multiple={true}
                id="combo-box-demo"
                options={
                  !fundNameLoading && fundNamError === null
                    ? allFundNames.map((item) => {
                        return {
                          label: item.fund_name,
                          id: item.fund_id,
                        };
                      })
                    : []
                }
                size={"small"}
                onChange={(event: any, newValue: any[] | null) => {
                  setFund_id(newValue as any[]);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                // disableClearable
                value={fund_id}
                renderInput={(params) => (
                  <TextField {...params} label="All Funds" />
                )}
              />
            </FormControl>

            <FormControl
              className={styles.flexibleformControlFullWidth}
              size="small"
            >
              <Autocomplete
                multiple={true}
                id="combo-box-demo"
                options={challenge_dropDownOption}
                size={"small"}
                onChange={(event: any, newValue: any[] | null) => {
                  setChallenge_id(newValue as any[]);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                //disableClearable
                value={challenge_id}
                renderInput={(params) => (
                  <TextField {...params} label="All Challenges" />
                )}
              />
            </FormControl>
          </div>
        </div>

        <div className={styles.flexibleControlRow}>
          <FormControl className={styles.flexibleformControl} size="small">
            <InputLabel className={styles.flexSelectFont}>
              X-axis Property
            </InputLabel>
            <Select
              className={styles.flexSelectFont}
              value={selectedX}
              label={"X-axis Property"}
              onChange={(event) =>
                event.target.value != selectedY
                  ? setSelectedX(event.target.value)
                  : alert("X and Y property cannot the same")
              }
              defaultValue="average_rating"
            >
              {names.map((c: any, i: any) => (
                <MenuItem value={c} key={i}>
                  {underscoreToCap(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={styles.flexibleformControl} size="small">
            <InputLabel className={styles.flexSelectFont}>
              Y-axis Property
            </InputLabel>
            <Select
              className={styles.flexSelectFont}
              value={selectedY}
              label={"Y-axis Property"}
              onChange={(event) =>
                event.target.value != selectedX
                  ? setSelectedY(event.target.value)
                  : alert("X and Y property cannot the same")
              }
              defaultValue="total_result_votes"
            >
              {names.map((c: any, i: any) => (
                <MenuItem value={c} key={i}>
                  {underscoreToCap(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={styles.flexibleformControl} size="small">
            <InputLabel className={styles.flexSelectFont}>
              Size property
            </InputLabel>
            <Select
              className={styles.flexSelectFont}
              value={selectedZ}
              label={underscoreToCap(selectedZ)}
              onChange={(event) => setSelectedZ(event.target.value)}
              defaultValue="funded_amount"
            >
              {z_names.map((c: any, i: any) => (
                <MenuItem value={c} key={i}>
                  {underscoreToCap(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {(!error || loading) && (
          <BubbleChart
            xLabel={underscoreToCap(selectedX)}
            yLabel={underscoreToCap(selectedY)}
            zLabel={underscoreToCap(selectedZ)}
            chartData={data}
            jumpToProposal={(proposalID: number) => jumpToProposal(proposalID)}
          />
        )}

        {!loading && error && (
          <div className="horizontalCenter">
            <Paper elevation={4} className={styles.chartErrorBox}>
              <Typography>
                No data for your selection, please try again.
              </Typography>
              <Button variant="contained" onClick={errorHandler}>
                Try again
              </Button>
            </Paper>
          </div>
        )}

        {getChartBottomIcons(showMaxButton, setOpen)}
        {getChartInfoIcons(desc["proposer"]["bubble"])}
      </Paper>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className={styles.fullScreenChart}>{content(false)}</Box>
      </Modal>
      {content(true)}
    </>
  );
};

export default ProposerBubblePlot;
